// Per-call cost estimator for the voice models. The numbers below are the
// public price-list rates as of October 2026; if the providers re-price,
// update this file (and only this file).
//
// Sources:
//   * OpenAI gpt-realtime-2.1 / -mini:  developers.openai.com/api/docs/pricing
//   * Gemini 3.8 Live (both variants):  ai.google.dev/gemini-api/docs/pricing
//
// Each rate is "USD per 1 token", computed as price_per_million / 1_000_000
// so that math elsewhere stays a single multiplication. Audio tokens follow
// the providers' own units: OpenAI = 1 token / 100ms user, 1 / 50ms agent;
// Gemini = its own internal mapping returned via promptTokensDetails.

const M = 1_000_000;

const GEMINI_LIVE_RATES = Object.freeze({
  text_input:    0.75 / M,
  text_output:   4.50 / M,
  audio_input:   3.00 / M,
  audio_output:  12.00 / M,
  // Thinking tokens are billed at output rate (Google docs:
  // "Thinking tokens: Included in output pricing"). Only the
  // extended-thinking model reports them.
  thoughts:      4.50 / M,
  // googleSearch grounding: free for the first 5,000 prompts per month
  // (shared across Gemini 3 family). Beyond that, $14 per 1,000 search
  // queries. The Live API does not report a per-turn "did I search"
  // counter, so we cannot bill grounding precisely from in-flight usage,
  // and surfacing it as an estimate would be misleading. We display
  // grounding cost as "not tracked" in the UI.
});

// Keyed by catalogue model id (config/ai-models.json).
export const PRICING = Object.freeze({
  'gpt-realtime-2.1': {
    text_input:    4.00 / M,
    text_cached:   0.40 / M,
    text_output:   24.00 / M,
    audio_input:   32.00 / M,
    audio_cached:  0.40 / M,
    audio_output:  64.00 / M,
  },
  'gpt-realtime-2.1-mini': {
    text_input:    0.60 / M,
    text_cached:   0.06 / M,
    text_output:   2.40 / M,
    audio_input:   10.00 / M,
    audio_cached:  0.30 / M,
    audio_output:  20.00 / M,
  },
  'gemini-3.8-live':                   GEMINI_LIVE_RATES,
  'gemini-3.8-live-extended-thinking': GEMINI_LIVE_RATES,
  // Voice billed per second ($0.05/min) plus the delegated backend's tokens,
  // priced here at the default backend gpt-6-luna. The server records the
  // authoritative cost in ai_usage at hang-up.
  'gpt-live-1': {
    per_second:     0.05 / 60,
    backend_input:  0.10 / M,
    backend_cached: 0.01 / M,
    backend_output: 0.50 / M,
  },
});

// ─── GPT-Live usage shape ───────────────────────────────────────────────────
// live = { seconds, input, cached, output }: the latest cumulative voice
// seconds (session.usage.updated) plus backend tokens summed once per
// response from response.event / response.completed.
export function costFromLiveUsage(live) {
  if (!live) return zeroBreakdown();
  const r = PRICING['gpt-live-1'];
  const seconds = num(live.seconds);
  const backend_in_cached = num(live.cached);
  const backend_in  = Math.max(0, num(live.input) - backend_in_cached);
  const backend_out = num(live.output);
  const cost =
      seconds           * r.per_second
    + backend_in        * r.backend_input
    + backend_in_cached * r.backend_cached
    + backend_out       * r.backend_output;
  return { cost, tokens: { seconds, backend_in, backend_in_cached, backend_out } };
}

// An unknown or missing model id prices at the provider's default model.
function ratesFor(model, fallback) {
  return PRICING[model] || PRICING[fallback];
}

// ─── OpenAI usage shape ─────────────────────────────────────────────────────
// response.done event delivers `response.usage` with this nested shape (GA,
// May 2026 — verified against the live API):
//   {
//     total_tokens, input_tokens, output_tokens,
//     input_token_details: {
//       text_tokens, audio_tokens, cached_tokens,
//       cached_tokens_details: { text_tokens, audio_tokens }
//     },
//     output_token_details: { text_tokens, audio_tokens }
//   }
// Returns { cost (USD), tokens: { text_in, audio_in, text_in_cached,
//          audio_in_cached, text_out, audio_out } } so the caller can also
// surface per-modality breakdown if it wants.
export function costFromOpenAIUsage(usage, model) {
  if (!usage) return zeroBreakdown();
  const inDet  = usage.input_token_details  || {};
  const outDet = usage.output_token_details || {};
  const cached = inDet.cached_tokens_details || {};

  const text_in_cached   = num(cached.text_tokens);
  const audio_in_cached  = num(cached.audio_tokens);
  const text_in          = Math.max(0, num(inDet.text_tokens)  - text_in_cached);
  const audio_in         = Math.max(0, num(inDet.audio_tokens) - audio_in_cached);
  const text_out         = num(outDet.text_tokens);
  const audio_out        = num(outDet.audio_tokens);

  const r = ratesFor(model, 'gpt-realtime-2.1');
  const cost =
      text_in        * r.text_input
    + text_in_cached * r.text_cached
    + audio_in       * r.audio_input
    + audio_in_cached* r.audio_cached
    + text_out       * r.text_output
    + audio_out      * r.audio_output;

  return {
    cost,
    tokens: { text_in, audio_in, text_in_cached, audio_in_cached, text_out, audio_out },
  };
}

// ─── Gemini usage shape ─────────────────────────────────────────────────────
// usageMetadata delivered with each `turnComplete`-bearing serverContent
// message has this shape:
//   {
//     promptTokenCount, responseTokenCount, totalTokenCount,
//     promptTokensDetails:   [{ modality: 'TEXT'|'AUDIO'|..., tokenCount }],
//     responseTokensDetails: [{ modality: 'TEXT'|'AUDIO'|..., tokenCount }],
//     thoughtsTokenCount     // present only when thinking is enabled
//   }
export function costFromGeminiUsage(usageMetadata, model) {
  if (!usageMetadata) return zeroBreakdown();
  const promptByMod   = byModality(usageMetadata.promptTokensDetails);
  const responseByMod = byModality(usageMetadata.responseTokensDetails);

  const text_in   = num(promptByMod.TEXT);
  const audio_in  = num(promptByMod.AUDIO);
  const text_out  = num(responseByMod.TEXT);
  const audio_out = num(responseByMod.AUDIO);
  const thoughts  = num(usageMetadata.thoughtsTokenCount);

  const r = ratesFor(model, 'gemini-3.8-live');
  const cost =
      text_in   * r.text_input
    + audio_in  * r.audio_input
    + text_out  * r.text_output
    + audio_out * r.audio_output
    + thoughts  * r.thoughts;

  return {
    cost,
    tokens: { text_in, audio_in, text_out, audio_out, thoughts },
  };
}

// ─── Aggregation + formatting ──────────────────────────────────────────────
// addBreakdowns({...}, {...}) — sum two breakdowns into a new one.
export function addBreakdowns(a, b) {
  if (!a) return b || zeroBreakdown();
  if (!b) return a;
  const tokens = {};
  for (const k of new Set([...Object.keys(a.tokens || {}), ...Object.keys(b.tokens || {})])) {
    tokens[k] = num(a.tokens?.[k]) + num(b.tokens?.[k]);
  }
  return { cost: num(a.cost) + num(b.cost), tokens };
}

// Format a USD float for display. The customer wants precision — sub-cent
// calls are common in early testing, so we never round to dollars. Format
// rules:
//   * $0 -> "-" (not interesting before first turn)
//   * cost < $0.01 -> "0.1234 ¢" (cents with 4 decimals for sub-cent reads)
//   * cost >= $0.01 -> "$0.0142" (dollars with 4 decimals)
export function formatCost(usd) {
  if (!usd || usd <= 0) return '-';
  if (usd < 0.01) return `${(usd * 100).toFixed(4)} ¢`;
  return `$${usd.toFixed(4)}`;
}

// ─── Internals ──────────────────────────────────────────────────────────────
function num(v) { return Number.isFinite(v) ? v : 0; }
function byModality(arr) {
  const out = {};
  if (!Array.isArray(arr)) return out;
  for (const d of arr) if (d && d.modality) out[d.modality] = num(d.tokenCount);
  return out;
}
function zeroBreakdown() { return { cost: 0, tokens: {} }; }
